import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  IconButton,
  TextField,
  Tooltip,
} from "@mui/material";
import InsertChartRoundedIcon from "@mui/icons-material/InsertChartRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import KeyboardArrowUpRoundedIcon from "@mui/icons-material/KeyboardArrowUpRounded";
import AutoFixHighRoundedIcon from "@mui/icons-material/AutoFixHighRounded";
import InsightChartRenderer from "./InsightChartRenderer";
import {
  createDashboardChart,
  deleteDashboardChart,
  fetchDashboardCharts,
  getAgentBaseUrl,
  updateDashboardChart,
} from "../../../../../features/ai-assistant/conversationClient";
import "./MenuInsightDashboard.css";

function localId() {
  return typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `local-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function normalizeStoredChart(row, fallbackPosition = 0) {
  const spec = row.chart_spec || row.chartSpec || row.chartSpecJson || row;
  return {
    id: row.id || spec.id || localId(),
    title: row.title || spec.title || "Gráfico ITG Insight",
    chartSpec: spec,
    position: Number.isFinite(Number(row.position)) ? Number(row.position) : fallbackPosition,
    persisted: Boolean(row.id),
  };
}

export default function MenuInsightDashboard({ scappContext, aiWebhookUrl, translate }) {
  const t = useCallback((key, fallback) => {
    const value = typeof translate === "function" ? translate(key) : key;
    return value && value !== key ? value : fallback;
  }, [translate]);

  const username = localStorage.getItem("username") || "anonymous";
  const agentBaseUrl = useMemo(() => getAgentBaseUrl(aiWebhookUrl), [aiWebhookUrl]);
  const [charts, setCharts] = useState([]);
  const [notice, setNotice] = useState("");

  useEffect(() => {
    if (!agentBaseUrl || !username) return;
    let cancelled = false;
    fetchDashboardCharts({ agentBaseUrl, username, service: "scapp", view: "insight" })
      .then((rows) => {
        if (!cancelled) setCharts((rows || []).map(normalizeStoredChart));
      })
      .catch(() => {
        if (!cancelled) setNotice(t("insight_local_mode", "Modo local"));
      });
    return () => {
      cancelled = true;
    };
  }, [agentBaseUrl, t, username]);

  const persistChart = useCallback(
    async (chart) => {
      if (!agentBaseUrl || !username) return chart;
      const saved = await createDashboardChart({
        agentBaseUrl,
        username,
        service: "scapp",
        view: "insight",
        title: chart.title,
        chartSpec: chart.chartSpec,
        position: chart.position,
      });
      return normalizeStoredChart(saved, chart.position);
    },
    [agentBaseUrl, username]
  );

  useEffect(() => {
    const handler = async (event) => {
      const patch = event.detail || {};
      if (patch.action !== "propose_chart" || !patch.chartSpec) return;
      const draft = normalizeStoredChart(
        {
          chartSpec: patch.chartSpec,
          title: patch.chartSpec.title,
          position: charts.length,
        },
        charts.length
      );
      setCharts((current) => [...current, draft]);
      try {
        const saved = await persistChart(draft);
        setCharts((current) => current.map((chart) => (chart.id === draft.id ? saved : chart)));
        setNotice(t("insight_chart_added", "Gráfico añadido"));
      } catch {
        setNotice(t("insight_chart_added_local", "Gráfico añadido localmente"));
      }
    };
    window.addEventListener("itg-insight-dashboard-patch", handler);
    return () => window.removeEventListener("itg-insight-dashboard-patch", handler);
  }, [charts.length, persistChart, t]);

  const handleDelete = async (chart) => {
    setCharts((current) => current.filter((item) => item.id !== chart.id));
    if (chart.persisted && agentBaseUrl) {
      await deleteDashboardChart({ agentBaseUrl, chartId: chart.id, username }).catch(() => null);
    }
  };

  const handleTitleChange = async (chart, title) => {
    setCharts((current) =>
      current.map((item) => (item.id === chart.id ? { ...item, title } : item))
    );
    if (chart.persisted && agentBaseUrl) {
      await updateDashboardChart({
        agentBaseUrl,
        chartId: chart.id,
        username,
        updates: { title },
      }).catch(() => null);
    }
  };

  const moveChart = async (chart, direction) => {
    const ordered = [...charts].sort((a, b) => a.position - b.position);
    const index = ordered.findIndex((item) => item.id === chart.id);
    const nextIndex = index + direction;
    if (index < 0 || nextIndex < 0 || nextIndex >= ordered.length) return;
    [ordered[index], ordered[nextIndex]] = [ordered[nextIndex], ordered[index]];
    const next = ordered.map((item, position) => ({ ...item, position }));
    setCharts(next);
    if (agentBaseUrl) {
      await Promise.all(
        next.map((item) =>
          item.persisted
            ? updateDashboardChart({
                agentBaseUrl,
                chartId: item.id,
                username,
                updates: { position: item.position },
              }).catch(() => null)
            : Promise.resolve()
        )
      );
    }
  };

  const openAssistant = () => {
    window.dispatchEvent(new CustomEvent("itg-insight-open-assistant"));
  };

  const orderedCharts = useMemo(
    () => [...charts].sort((a, b) => a.position - b.position),
    [charts]
  );

  return (
    <section className="insight-dashboard">
      <div className="insight-dashboard-header">
        <div>
          <h2>ITG Insight</h2>
          <p>{t("insight_dashboard_subtitle", "Dashboard personalizado SCAPP")}</p>
        </div>
        <div className="insight-dashboard-actions">
          {notice && <span>{notice}</span>}
          <Tooltip title={t("open_assistant", "Abrir asistente")}>
            <IconButton className="insight-action" onClick={openAssistant}>
              <AutoFixHighRoundedIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={t("add_chart", "Añadir gráfico")}>
            <IconButton className="insight-action" onClick={openAssistant}>
              <InsertChartRoundedIcon />
            </IconButton>
          </Tooltip>
        </div>
      </div>

      {orderedCharts.length === 0 ? (
        <div className="insight-empty-state">
          <h3>{t("insight_empty_title", "Sin gráficos personalizados")}</h3>
          <p>{t("insight_empty_body", "Pide un gráfico desde ITG Insight para añadirlo aquí.")}</p>
        </div>
      ) : (
        <div className="insight-chart-grid">
          {orderedCharts.map((chart, index) => (
            <article key={chart.id} className="insight-chart-card">
              <div className="insight-chart-card-header">
                <TextField
                  value={chart.title}
                  variant="standard"
                  onChange={(event) => handleTitleChange(chart, event.target.value)}
                  InputProps={{ disableUnderline: true }}
                  sx={{
                    input: {
                      color: "#f3f5f8",
                      fontWeight: 700,
                      fontSize: "0.95rem",
                    },
                  }}
                />
                <Box sx={{ display: "flex", gap: 0.25 }}>
                  <Tooltip title={t("move_up", "Subir")}>
                    <span>
                      <IconButton
                        className="insight-card-action"
                        disabled={index === 0}
                        onClick={() => moveChart(chart, -1)}
                      >
                        <KeyboardArrowUpRoundedIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title={t("move_down", "Bajar")}>
                    <span>
                      <IconButton
                        className="insight-card-action"
                        disabled={index === orderedCharts.length - 1}
                        onClick={() => moveChart(chart, 1)}
                      >
                        <KeyboardArrowDownRoundedIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title={t("delete", "Eliminar")}>
                    <IconButton className="insight-card-action" onClick={() => handleDelete(chart)}>
                      <DeleteRoundedIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </div>
              <div className="insight-chart-canvas">
                <InsightChartRenderer chartSpec={chart.chartSpec} scappContext={scappContext} />
              </div>
              <p className="insight-chart-note">{chart.chartSpec.coherenceNote}</p>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}
